import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TradeAccordion } from './TradeAccordion'
import { EquipmentFilterBar, type FilterState } from './EquipmentFilterBar'
import { useAssetTypes } from '../../hooks/useQueries'
import {
  SPEC_TRADE_CODES,
  type EquipmentTabResponse,
  type EquipmentTradeBucket,
} from '../../api/equipment-tab-types'
import { EmptyState } from '../ui/empty-state'

interface ZoneBEquipmentByTradeProps {
  org: string
  data?: EquipmentTabResponse
  loading?: boolean
  /** URL-synced filter state. */
  filters: FilterState
  onFiltersChange: (next: FilterState) => void
  /**
   * Fired when a per-trade `+` button is clicked. The parent EquipmentTab
   * opens the AddEquipmentDialog with this trade pre-selected.
   */
  onAddEquipment?: (tradeCode: string) => void
  /**
   * Trade codes that can be created under the current asset (location scoping).
   * `undefined` means "no restriction". A trade not in this list renders
   * without its `+` button, and is hidden entirely when it also has no
   * equipment (so existing equipment is never hidden).
   */
  allowedTradeCodes?: string[]
  /** Opens "Add a component" for an equipment row (spec 2026-10-27-17). */
  onAddComponent?: (assemblyUid: string) => void
}

/**
 * Bottom zone — equipment grouped by trade. Spec §6.
 *
 * The list of trades is read at runtime from
 *   GET /api/v1/organizations/{orgCode}/asset-types
 * filtered to rows whose `kind === "trade"` (the canonical discriminator
 * exposed since spec 2026-05-05-02). The `code.startsWith("trade_")` fallback
 * remains as a safety net for older API responses that might not carry `kind`.
 */
export function ZoneBEquipmentByTrade({
  org,
  data,
  loading,
  filters,
  onFiltersChange,
  onAddEquipment,
  allowedTradeCodes,
  onAddComponent,
}: ZoneBEquipmentByTradeProps) {
  const { t, i18n } = useTranslation('assets')
  const { data: allAssetTypes = [] } = useAssetTypes(org)
  const allowedSet = useMemo(
    () => (allowedTradeCodes ? new Set(allowedTradeCodes) : null),
    [allowedTradeCodes],
  )

  // Trade list — pulled from asset-types, never hardcoded.
  const tradeAssetTypes = useMemo(() => {
    return allAssetTypes
      .filter((at) => {
        const kind = (at as { kind?: string }).kind
        if (kind === 'trade') return true
        if (kind) return false
        return typeof at.code === 'string' && at.code.startsWith('trade_')
      })
      .sort((a, b) => {
        // Stable order: spec order first, then anything else alphabetically.
        const ai = SPEC_TRADE_CODES.indexOf(a.code as never)
        const bi = SPEC_TRADE_CODES.indexOf(b.code as never)
        if (ai !== -1 && bi !== -1) return ai - bi
        if (ai !== -1) return -1
        if (bi !== -1) return 1
        return a.code.localeCompare(b.code)
      })
  }, [allAssetTypes])

  // Merge backend buckets with the trade list so every trade renders even
  // when the backend returns an empty `equipment_by_trade`.
  const buckets: EquipmentTradeBucket[] = useMemo(() => {
    const byCode = new Map<string, EquipmentTradeBucket>()
    for (const bucket of data?.equipment_by_trade ?? []) {
      byCode.set(bucket.trade_code, bucket)
    }
    return tradeAssetTypes.map((at) => {
      const fromApi = byCode.get(at.code)
      const localizedLabel =
        typeof at.name === 'object' && at.name !== null
          ? (at.name as Record<string, string>)[i18n.language] ||
            (at.name as Record<string, string>).en ||
            (at.name as Record<string, string>).fr ||
            at.code
          : (at.name as string) || at.code
      return {
        trade_code: at.code,
        // Prefer the locally-resolved label so the page reacts to the user's
        // i18n preference even when the backend returns a single-locale
        // `trade_label`. Fall back to the API string when no asset-type is
        // matched (defensive — should not happen in practice).
        trade_label: localizedLabel || fromApi?.trade_label || at.code,
        trade_emoji: fromApi?.trade_emoji ?? at.emoji ?? null,
        trade_icon: fromApi?.trade_icon ?? at.icon ?? null,
        count: fromApi?.count ?? 0,
        condition_rollup:
          fromApi?.condition_rollup ?? {
            poor: 0,
            end_of_life: 0,
            unassessed: 0,
          },
        equipment: fromApi?.equipment ?? [],
      }
    })
  }, [tradeAssetTypes, data?.equipment_by_trade, i18n.language])

  // Location scoping: hide trades that cannot be created here AND carry no
  // equipment. Trades that already hold equipment are always kept (never hide
  // real data); they just lose their `+` affordance.
  const scopedBuckets = useMemo(() => {
    if (!allowedSet) return buckets
    return buckets.filter(
      (b) => allowedSet.has(b.trade_code) || b.count > 0 || (b.equipment?.length ?? 0) > 0,
    )
  }, [buckets, allowedSet])

  // The visible buckets are the location-scoped buckets — the per-trade chip
  // filter was removed as redundant with the trade accordions below.
  const visibleBuckets = scopedBuckets

  const totalEquipment = data?.totals?.equipment_count ?? 0

  return (
    <div
      className="rounded-lg border border-border bg-card p-4"
      data-testid="equipment-tab-zone-b"
    >
      <EquipmentFilterBar
        state={filters}
        onChange={onFiltersChange}
      />

      {/* Empty hint banner */}
      {!loading && totalEquipment === 0 && (
        <EmptyState
          variant="inline"
          className="mb-3 py-4"
          title={t('equipment_tab.zone_b.empty_hint')}
          data-testid="equipment-tab-zone-b-empty-hint"
        />
      )}

      {loading ? (
        <div
          className="flex items-center justify-center py-8 text-sm text-muted-foreground"
          data-testid="equipment-tab-zone-b-loading"
        >
          <div className="motion-safe:animate-spin rounded-full h-5 w-5 border-b-2 border-primary mr-2" />
          {t('equipment_tab.loading')}
        </div>
      ) : visibleBuckets.length === 0 ? (
        <EmptyState
          variant="inline"
          live
          className="py-3"
          title={t('equipment_tab.zone_b.no_trades_in_filter')}
          data-testid="equipment-tab-zone-b-no-trades"
        />
      ) : (
        <div className="space-y-2">
          {visibleBuckets.map((bucket) => (
            <TradeAccordion
              key={bucket.trade_code}
              org={org}
              bucket={bucket}
              onAddEquipment={onAddEquipment}
              creatable={!allowedSet || allowedSet.has(bucket.trade_code)}
              onAddComponent={onAddComponent}
            />
          ))}
        </div>
      )}
    </div>
  )
}
